import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Student } from 'core/models/student';
import { Client } from 'core/models/client';
import { ModalComponent } from 'core/components/modal/modal.component';
import {
  StudentsService,
  NotificationsService,
  PopUpService,
  StoreService,
  UtilsService,
  ProfileService,
  SecurityService
} from 'core/services';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { StudentEmailEditorComponent } from '../student-email-editor/student-email-editor.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'lms-student-detail',
  templateUrl: './student-detail.component.html',
  styleUrls: ['./student-detail.component.scss']
})
export class StudentDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('studentEmailEditor', { static: false }) public studentEmailEditor: StudentEmailEditorComponent;
  public student: Student;
  public client: Client;
  public tabsManager: any | Subscription = new Subscription();
  public modal: ModalComponent;
  public studentEmailsListId = 'STUDENT_EMAILS_LIST';
  public studentLicensesListId = 'STUDENT_LICENSES_LIST';
  public studentSurveysListId = 'STUDENT_SURVEYS_LIST';
  public filters: Object;
  public notificationFilter: Object;
  public timezone: any;
  public toolbarConfig: any;
  @ViewChild('tabGroup', { static: false }) public tabGroup: MatTabGroup;

  constructor(
    public studentsService: StudentsService,
    public notificationsService: NotificationsService,
    public profileService: ProfileService,
    public route: ActivatedRoute,
    public location: Location,
    public translate: TranslateService,
    public popupService: PopUpService,
    public titleService: Title,
    private cd: ChangeDetectorRef,
    private securityService: SecurityService
  ) {}

  public ngOnInit() {
    this.toolbarConfig = {
      list: {
        listId: this.studentEmailsListId,
        download: this.profileService.canExecute('emails.download'),
        skeletor: true
      }
    };
    this.route.data.subscribe((data) => {
      this.student = data['student'];
      this.client = data['client'];
      this.filters = { userId: this.student.id, kind: 'normal' };
      this.notificationFilter = { entityId: this.student.id };
      this.titleService.setTitle(
        (this.student.name ? this.student.name : this.student.id) +
          ' - ' +
          this.translate.instant('pageTitle.students') +
          ' - ' +
          this.translate.instant('pageTitle.gamelearn')
      );
      this.timezone = this.client.timezone;
    });
  }

  public ngAfterViewInit(): void {
    this.tabsManager = UtilsService.createTabsManager(this.route, this.tabGroup);
    this.cd.detectChanges();
  }

  public ngOnDestroy(): void {
    this.tabsManager.unsubscribe();
  }

  public setTab(selectedTab: MatTabChangeEvent): void {
    this.tabsManager.setTab(selectedTab);
  }

  public goBack(): void {
    this.location.back();
  }

  public onUpdateStudent(student: Student): void {
    this.student = student;
    StoreService.merge(this.studentLicensesListId, { active: true });
  }

  public deleteStudent(): void {
    const fullName =
      this.student.name && this.student.surname ? `${this.student.name} ${this.student.surname}` : this.student.email;
    this.popupService
      .confirmDelete({
        title: this.translate.instant('actions.deleteStudent'),
        text: this.translate.instant('actions.deleteStudentMessage', { student: fullName }),
        alert: this.translate.instant('actions.notUndo'),
        size: 'small',
        confirmButtonText: this.translate.instant('actions.delete')
      })
      .then(() => this.studentsService.delete(this.student.id))
      .then(() => this.goBack())
      .catch((err) => {
        if (err !== 'cancel') {
          this.popupService.showError({
            title: this.translate.instant('students.deleteError'),
            text: this.translate.instant('students.deleteErrorMessage')
          });
        }
      });
  }

  public anonymizeStudent(): void {
    const fullName =
      this.student.name && this.student.surname ? `${this.student.name} ${this.student.surname}` : this.student.email;
    this.popupService
      .confirm({
        title: this.translate.instant('actions.anonymizeStudent'),
        text: this.translate.instant('students.confirmAnonymizeMessage', { student: fullName }),
        alert: this.translate.instant('actions.notUndo'),
        size: 'small',
        confirmButtonText: this.translate.instant('actions.anonymizeStudent')
      })
      .then(() => this.studentsService.anonymize(this.student.id))
      .then(() => this.goBack())
      .catch((err) => {
        if (err !== 'cancel') {
          this.popupService.showError({
            title: this.translate.instant('students.anonymizeError'),
            text: err.serverMessage
          });
        }
      });
  }

  public updateEmail(): void {
    this.studentEmailEditor.open();
  }

  public onStudentEmailChanged(newEmail: string): void {
    this.onUpdateStudent({ ...this.student, email: newEmail });
    this.cd.detectChanges();
  }

  public sendWelcomeMail(): void {
    this.popupService
      .confirm({
        title: this.translate.instant('notifications.manual.title'),
        text: this.translate.instant('notifications.manual.description', { name: 'welcome' })
      })
      .then(() => {
        const notificationData = {
          student: this.student,
          clientType: this.client.type,
          clientDisplayName: this.client.displayName
        };
        this.notificationsService.sendWelcomeStudentNotification(notificationData).then();
      })
      .catch(() => {});
  }

  /**
   * Reset the student password
   */
  public resetStudentPasswordDialog(): void {
    const studentInfo =
      this.student.name && this.student.surname ? `${this.student.name} ${this.student.surname}` : this.student.email;
    this.popupService
      .confirm({
        title: this.translate.instant('actions.resetPassword'),
        text: this.translate.instant('students.resetPasswordMessage', { student: studentInfo }),
        size: 'medium',
        confirmButtonText: this.translate.instant('actions.confirm')
      })
      .then(() => this.securityService.resetUserPassword(this.student.email))
      .then(() => this.goBack())
      .catch((err) => {
        if (err !== 'cancel') {
          this.popupService.showError({
            title: this.translate.instant('students.resetPasswordError'),
            text: err.serverMessage
          });
        }
      });
  }
}
